import { useState, useRef, useEffect } from 'react';
import { User, ChevronDown } from 'lucide-react';

/**
 * Autocomplete input for delegate names.
 * Shows suggestions from previously-used delegate names.
 */
export default function DelegateInput({
  value,
  onChange,
  delegates = [],
  placeholder = "Delegated to...",
  className = "",
  autoFocus = false
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value || '');
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  // Sync internal state with external value
  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  // Filter suggestions based on input
  const filteredDelegates = delegates.filter(
    d => d.toLowerCase().includes(inputValue.toLowerCase())
  );

  // Show suggestions when there are matches and input is focused
  const showSuggestions = isOpen && (filteredDelegates.length > 0 || delegates.length > 0);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    onChange(newValue);
    setIsOpen(true);
  };

  const handleSelect = (delegate) => {
    setInputValue(delegate);
    onChange(delegate);
    setIsOpen(false);
    inputRef.current?.focus();
  };

  const handleFocus = () => {
    setIsOpen(true);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
    } else if (e.key === 'ArrowDown' && showSuggestions) {
      e.preventDefault();
      // Focus first suggestion if available
      const firstButton = containerRef.current?.querySelector('.delegate-suggestion');
      firstButton?.focus();
    }
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          placeholder={placeholder}
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          autoFocus={autoFocus}
          className="w-full py-1.5 px-2 pr-7 bg-surface-card border border-border rounded text-xs text-text-primary outline-none focus:border-accent focus:ring-1 focus:ring-accent-muted transition-all"
        />
        {delegates.length > 0 && (
          <button
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            className="absolute right-1 top-1/2 -translate-y-1/2 p-1 text-text-muted hover:text-text-secondary cursor-pointer bg-transparent border-none"
          >
            <ChevronDown size={12} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </button>
        )}
      </div>

      {/* Suggestions Dropdown */}
      {showSuggestions && (
        <div className="absolute z-50 mt-1 w-full max-h-32 overflow-y-auto bg-surface-card border border-border rounded-lg shadow-lg">
          {inputValue && !delegates.includes(inputValue) && (
            <div className="px-2 py-1.5 text-[10px] text-text-muted border-b border-border">
              Type to add new or select existing
            </div>
          )}
          {(inputValue ? filteredDelegates : delegates).map((delegate) => (
            <button
              key={delegate}
              type="button"
              onClick={() => handleSelect(delegate)}
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  const next = e.target.nextElementSibling;
                  if (next) next.focus();
                } else if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  const prev = e.target.previousElementSibling;
                  if (prev && prev.classList.contains('delegate-suggestion')) {
                    prev.focus();
                  } else {
                    inputRef.current?.focus();
                  }
                } else if (e.key === 'Escape') {
                  setIsOpen(false);
                  inputRef.current?.focus();
                }
              }}
              className="delegate-suggestion w-full flex items-center gap-2 px-2 py-1.5 text-left text-xs text-text-primary hover:bg-surface-hover focus:bg-surface-hover cursor-pointer bg-transparent border-none outline-none transition-colors"
            >
              <User size={12} className="text-text-muted" />
              <span>{delegate}</span>
            </button>
          ))}
          {filteredDelegates.length === 0 && inputValue && delegates.length > 0 && (
            <div className="px-2 py-1.5 text-[10px] text-text-muted italic">
              No matches - press Enter to use "{inputValue}"
            </div>
          )}
        </div>
      )}
    </div>
  );
}
